import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { TRACKING_EVENT_CONTEXT_KEYS } from '@/hotdesk-ui/constants/tracking-keys';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { User } from 'hotdesk_typescript';
import { fetchUserBookingStatsForTracking } from './booking-stats-for-tracking.service';
import { fetchNearestWorkspaceNameForTracking } from './nearest-workspace-for-tracking.service';
import { posthogEvents } from './posthog.service';
import type { ProviderTrackingProps } from '@/hotdesk-ui/utils/provider';
import {
    buildUserTrackingProperties,
    type UserTrackingProperties
} from './user-tracking-properties.service';

export type Currency = { code: string };
export type TrackingConfig = {
    posthogApiKey: string;
    posthogHost?: string;
    environment: string;
    appVersion: string;
    currency: Currency;
};

type ParamValue = string | number | boolean | null | undefined;
type ParamMap = Record<string, ParamValue>;

interface BookingDetails {
    startDateTime?: string;
    endDateTime?: string;
    unit?: string;
    paymentMethod?: string;
    transactionValue?: number;
    finalPrice?: number;
    discountValue?: number;
    seats?: number;
}

interface BookingRecord {
    id?: string | number;
    status?: string;
    details?: BookingDetails;
}

interface ListingDetails {
    title?: string;
    workspace?: { title?: string };
}

interface FilterConfig {
    selectedListingTypes?: string;
    selectedFrequencies?: string;
    capacity?: string | number;
}

interface LocationConfig {
    lat?: number;
    lng?: number;
}

class Tracking {
    private userMap: ParamMap = {};

    // Remote Config flags
    public allowPosthog: boolean = false;
    public trackUserProperties: boolean = true;

    // Events fired before init() completes are buffered here and flushed on init
    private _initialized: boolean = false;
    private _queue: Array<() => void> = [];

    private get isProductionEnvironment(): boolean {
        const selfUrl = process.env.NEXT_PUBLIC_SELF_URL ?? '';
        return (
            selfUrl === 'https://www.hotdesk.com/' ||
            selfUrl === 'https://www.hotdesk.com'
        );
    }

    /**
     * Initializes the tracking service and sets up Posthog if allowed.
     * Production reads `tracking_feature` ({@link RemoteConfigKeys.ALLOW_POSTHOG});
     * non-production reads `tracking_feature_QA` ({@link RemoteConfigKeys.ALLOW_POSTHOG_QA}).
     * The userMap is seeded with: environment, app_version, currency, and a session uuid.
     * user_id, timestamp, and screen_url are populated at event-time via setUserId /
     * _newEventTimestamp / _addScreenUrl respectively.
     */
    public async init(): Promise<void> {
        const trackingToggleKey = this.isProductionEnvironment
            ? RemoteConfigKeys.ALLOW_POSTHOG
            : RemoteConfigKeys.ALLOW_POSTHOG_QA;
        const enableTracking = await fetchRemoteConfig(
            trackingToggleKey,
            'boolean'
        );
        this.allowPosthog = enableTracking === true;

        posthogEvents.setup(this.allowPosthog);

        this._setEnvironment(process.env.NEXT_PUBLIC_SELF_URL || 'development');
        this._setAppVersion(process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0');
        this._initUuid();

        this._initialized = true;
        this._flushQueue();
    }

    private _flushQueue(): void {
        const pending = this._queue.splice(0);
        pending.forEach((fn) => fn());
    }

    private _initUuid(): void {
        const uuid =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : Math.random().toString(36).slice(2);
        this.addUserVariable('uuid', uuid);
    }

    /** Rejects bad keys (e.g. `[undefined]` → literal `"undefined"` after a removed TrackingKeys entry). */
    private _isValidPersonPropertyKey(key: string): boolean {
        if (!key || key === 'undefined') return false;
        if (TRACKING_EVENT_CONTEXT_KEYS.has(key)) return false;
        // Legacy duplicate of `phone`; do not send to PostHog.
        if (key === 'phone_number') return false;
        return true;
    }

    private _personPropsForPosthog(
        extra?: UserTrackingProperties
    ): Record<string, string | number | boolean> | undefined {
        const uid = this.userMap['user_id'];
        if (uid == null || uid === '') return undefined;

        const merged: ParamMap = { ...this.userMap };
        if (extra) {
            Object.assign(merged, extra);
        }

        const props: Record<string, string | number | boolean> = {};
        for (const [key, value] of Object.entries(merged)) {
            if (!this._isValidPersonPropertyKey(key)) continue;
            if (value === null || value === undefined) continue;
            if (typeof value === 'boolean' || typeof value === 'number') {
                props[key] = value;
                continue;
            }
            const s = String(value);
            if (s !== '') props[key] = s;
        }
        return props;
    }

    // --- State Management ---

    /**
     * Registers the signed-in user for event context and PostHog identify.
     * `name` is sent as trimmed `firstName` + space + `lastName` (Flutter-style person map).
     */
    public setUserId(
        id: string | number,
        person?: { firstName?: string | null; lastName?: string | null }
    ): void {
        const apply = () => {
            this.addUserVariable('user_id', String(id));
            const first = (person?.firstName ?? '').trim();
            const last = (person?.lastName ?? '').trim();
            const displayName = [first, last].filter(Boolean).join(' ');
            this.addUserVariable('name', displayName);
            this.addUserVariable('first_name', first);
            this.addUserVariable('last_name', last);
            if (this.allowPosthog) {
                posthogEvents.setUserId(String(id), this._personPropsForPosthog());
            }
        };
        if (!this._initialized) {
            this._queue.push(apply);
            return;
        }
        apply();
    }

    /** Clears user id and display-name fields from the tracking map and resets PostHog. */
    public clearAuthenticatedUser(): void {
        const apply = () => {
            for (const key of Object.keys(this.userMap)) {
                if (!TRACKING_EVENT_CONTEXT_KEYS.has(key)) {
                    delete this.userMap[key];
                }
            }
            if (this.allowPosthog) {
                posthogEvents.setUserId(undefined);
            }
        };
        if (!this._initialized) {
            this._queue.push(apply);
            return;
        }
        apply();
    }

    /**
     * Merges person properties into the tracking map and optionally syncs PostHog
     * (Flutter `addUserProperties(..., addToPosthog: true)`).
     */
    public addUserProperties(
        properties: UserTrackingProperties,
        options?: { addToPosthog?: boolean }
    ): void {
        const apply = () => {
            Object.entries(properties).forEach(([key, value]) => {
                if (!this._isValidPersonPropertyKey(key)) return;
                if (value !== null && value !== undefined) {
                    this.userMap[key] = value as ParamValue;
                }
            });
            delete this.userMap['phone_number'];
            delete this.userMap['undefined'];

            const userId = this.userMap['user_id'];
            if (options?.addToPosthog && this.allowPosthog && userId) {
                const person = this._personPropsForPosthog(properties);
                if (person && Object.keys(person).length > 0) {
                    posthogEvents.setUserId(String(userId), person);
                }
            }
        };

        if (!this._initialized) {
            this._queue.push(apply);
            return;
        }
        apply();
    }

    /**
     * Login / session refresh: fetch booking stats, build Flutter-style person profile,
     * identify in PostHog. Non-fatal if booking stats fail.
     */
    public syncAuthenticatedUserProfile(
        user: User,
        token: string,
        languageCode: string,
        geo?: GeoCodeResult | null
    ): Promise<void> {
        const userId = user.id;
        if (userId == null) return Promise.resolve();

        const run = async () => {
            const [stats, nearestWorkspace] = await Promise.all([
                fetchUserBookingStatsForTracking(
                    userId,
                    token,
                    languageCode
                ),
                fetchNearestWorkspaceNameForTracking(
                    geo,
                    languageCode,
                    user,
                    token
                )
            ]);
            this.setUserId(userId, {
                firstName: user.firstName,
                lastName: user.lastName
            });
            const properties = buildUserTrackingProperties(user, stats, {
                languageCode,
                geo,
                nearestWorkspace
            });
            this.addUserProperties(properties, { addToPosthog: true });
        };

        if (!this._initialized) {
            return new Promise((resolve) => {
                this._queue.push(() => {
                    void run().finally(() => resolve());
                });
            });
        }

        return run();
    }

    public addUserVariable(key: string, value: ParamValue): void {
        if (!key || key === 'undefined') return;
        this.userMap[key] = value;
    }

    private _getUserMap(): ParamMap {
        if (!this.trackUserProperties) return {};
        return { ...this.userMap };
    }

    private _newEventTimestamp(): void {
        this.addUserVariable('timestamp', Date.now());
    }

    private _addScreenUrl(screenUrl: string): void {
        this.addUserVariable('screen_url', screenUrl);
    }

    /**
     * Provider segmentation for the listing→booking funnel: `provider` is
     * the stable code from `getListingProviderCode` ("iwg"), and
     * `provider_brand` the marketing brand(s) from `metaData.iwgBrands`
     * ("Regus", "Regus, Spaces"). Both keys are emitted on every funnel
     * event — empty strings for native listings — so PostHog
     * filters/breakdowns never special-case a missing property. Build the
     * argument with `getProviderTrackingProps(listing)`.
     */
    private _providerMap(providerProps?: ProviderTrackingProps): ParamMap {
        return {
            provider: providerProps?.provider ?? '',
            provider_brand: providerProps?.provider_brand ?? ''
        };
    }

    /**
     * Creates a new map filtering nulls to empty strings (mimics _filterNullsToEmpty in Dart)
     */
    private _filterNullsToEmpty(parameters: ParamMap): Record<string, string | number | boolean> {
        const filtered: Record<string, string | number | boolean> = {};
        Object.keys(parameters).forEach((key) => {
            const value = parameters[key];
            if (value !== null && value !== undefined) {
                filtered[key] = typeof value === 'object'
                    ? JSON.stringify(value)
                    : (value as string | number | boolean);
            } else {
                filtered[key] = '';
            }
        });
        return filtered;
    }

    // --- Core Record Wrapper ---

    private _recordEvent(eventName: string, params: ParamMap): void {
        if (!this._initialized) {
            this._queue.push(() => this._recordEvent(eventName, params));
            return;
        }
        if (!this.allowPosthog) return;

        const filteredParams = this._filterNullsToEmpty(params);

        if (this.trackUserProperties && params['user_id']) {
            posthogEvents.setUserId(
                params['user_id'].toString(),
                this._personPropsForPosthog()
            );
        }

        posthogEvents.actionTaken(eventName, filteredParams);
    }

    private _setEnvironment(env: string): void {
        this.addUserVariable('environment', env);
    }

    private _setAppVersion(version: string): void {
        this.addUserVariable('app_version', version);
    }

    // ==========================================
    // BUSINESS EVENTS (Called from UI Components)
    // ==========================================

    // 1. Page Reviews

    public onScreenChange(screenType: string, screenUrl: string, source?: string): void {
        if (!this._initialized) {
            this._queue.push(() => this.onScreenChange(screenType, screenUrl, source));
            return;
        }
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const params = this._getUserMap();
        if (source) params['source'] = source;

        const filteredParams = this._filterNullsToEmpty(params);

        if (this.allowPosthog) {
            posthogEvents.onScreenChange(screenType, screenUrl, filteredParams);
        }
    }

    /**
     * Screen-change variant that accepts arbitrary extra properties.
     * Uses posthog $pageview so PostHog can build user-journey paths / funnels.
     * Used for any navigation step that the doc marks as "onScreenChangeV2".
     */
    public onScreenChangeV2(screenType: string, screenUrl: string, extraParams?: ParamMap): void {
        if (!this._initialized) {
            this._queue.push(() => this.onScreenChangeV2(screenType, screenUrl, extraParams));
            return;
        }
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const params: ParamMap = { ...this._getUserMap(), ...extraParams };
        const filteredParams = this._filterNullsToEmpty(params);

        if (this.allowPosthog) {
            posthogEvents.onScreenChange(screenType, screenUrl, filteredParams);
        }
    }

    //-------------------------------- Actions Taken --------------------------------

    // --- Workspace / listing discovery funnel ---

    /**
     * Cat 2 / Step 1 — User views workspace details page.
     * Properties: workspace_id, workspace_name, workspace_amenities (comma-sep names), USER_MAP
     */
    public onWorkspaceDetailsViewed(
        workspaceId: string | number,
        workspaceName: string,
        workspaceAmenities: string[],
        screenUrl: string
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('view_workspace_details', {
            ...this._getUserMap(),
            workspace_id: workspaceId.toString(),
            workspace_name: workspaceName,
            workspace_amenities: workspaceAmenities.join(', ')
        });
    }

    /**
     * Cat 2 / Step 2 — Listings inside workspace are viewed.
     * Properties: workspace_id, screenType, screenUrl, source="workspace_details", USER_MAP
     */
    public onWorkspaceListingsViewed(
        workspaceId: string | number,
        screenUrl: string,
        listingIds?: (string | number)[]
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('workspace_listings_viewed', {
            ...this._getUserMap(),
            workspace_id: workspaceId.toString(),
            source: 'workspace_details',
            listing_ids: listingIds?.map(String).join(', ') ?? ''
        });
    }

    /**
     * Cat 2 / Step 3 — User clicks a specific listing on the workspace page.
     * Properties: listing_id, screenType, screenUrl, source=workspaceTitle, USER_MAP
     */
    public onDiscoveryListingClicked(
        listingId: string | number,
        workspaceTitle: string,
        screenUrl: string
    ): void {
        this.onScreenChangeV2('listing_view', screenUrl, {
            listing_id: listingId.toString(),
            source: workspaceTitle
        });
    }

    /**
     * Cat 1 / Step 5 — User clicks a workspace card from Explore / home.
     * Properties: workspace_id, screenType, screenUrl, source="workspace_details", USER_MAP
     */
    public onExploreWorkspaceSelected(
        workspaceId: string | number,
        screenUrl: string
    ): void {
        this.onScreenChangeV2('workspace_details', screenUrl, {
            workspace_id: workspaceId.toString(),
            source: 'workspace_details'
        });
    }

    /**
     * Fired when a signed-in user clicks "Request workspaces based on your
     * search" from the empty-state on the explore page, or "Notify me when
     * we expand to {country}" from the landing-page banner. Captures the
     * full search context — coordinates, address, country, city, and which
     * input produced the location — so the team can prioritise expansion.
     * Properties: location ("lng,lat"), formatted_address, country, city,
     * location_source, filters (JSON string), email, screen_url, USER_MAP
     */
    public onWorkspaceRequestSubmitted(
        place: GeoCodeResult | null | undefined,
        filters: Record<string, string> | undefined,
        screenUrl: string,
        email?: string
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        const payload: ParamMap = {
            ...this._getUserMap(),
            location: `${place?.log ?? ''},${place?.lat ?? ''}`,
            formatted_address: place?.formatted_address ?? '',
            country: place?.country_sort ?? '',
            city: place?.city ?? '',
            location_source: place?.source ?? '',
            filters: JSON.stringify(filters || {}),
            email: email || ''
        };
        this._recordEvent('workspace_request_submitted', payload);
    }

    public onSearchStarted(screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const params = this._getUserMap();
        this._recordEvent('search_started', params);
    }

    public onSearchEntered(text: string, screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const params: ParamMap = { ...this._getUserMap(), search_text: text };
        this._recordEvent('search_entered', params);
    }

    public onLogin(method: string, screenUrl: string, email?: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const params: ParamMap = { ...this._getUserMap(), method };
        if (email) params['email'] = email;

        this._recordEvent('login', params);
    }

    public onBookingRequested(bookingId: string, transactionValue: number, workspaceName: string, screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const params: ParamMap = {
            ...this._getUserMap(),
            booking_id: bookingId,
            transaction_value: transactionValue,
            workspace_name: workspaceName,
        };

        this._recordEvent('booking', params);
    }

    public onError(type: string, message: string): void {
        this._newEventTimestamp();

        const params: ParamMap = {
            ...this._getUserMap(),
            error: message,
            error_type: type
        };

        this._recordEvent('application_error', params);
    }

    public onSignOut(screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        this._recordEvent('sign_out', this._getUserMap());

        this.clearAuthenticatedUser();
    }

    public locationPermitted(isPermitted: boolean, lat: number, lng: number, screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        if (isPermitted) {
            this.addUserVariable('location_pin', `${lat},${lng}`);
        }

        const params: ParamMap = { ...this._getUserMap(), granted: isPermitted };
        this._recordEvent('gps_location_permission', params);
    }

    public onDynamicLinkClicked(dynamicLink: string): void {
        this._newEventTimestamp();
        const params: ParamMap = { ...this._getUserMap(), dynamic_link: dynamicLink };
        this._recordEvent('dynamic_link_clicked', params);
    }

    public onMapGesture(lng: number, lat: number, zoom: number): void {
        this._newEventTimestamp();
        const screenUrl = `/map_gesture/?location=${lng},${lat}&zoom=${zoom}`;
        this._addScreenUrl(screenUrl);
        this._recordEvent('map_gesture', {
            ...this._getUserMap(),
            coordinate: `${lng},${lat}`,
            zoom_level: zoom
        });
    }

    public onMapGestureChanged(lat: number, lng: number, zoom: number, radius: number, gestureType: string, screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const params: ParamMap = {
            ...this._getUserMap(),
            gesture_type: gestureType,
            coordinate: JSON.stringify({ long: lng, lat: lat }),
            zoom_level: zoom,
            search_radius: radius
        };

        this._recordEvent('map_gesture', params);
    }

    public onFiltersChange(listingLength: number, filters: FilterConfig, location: LocationConfig, screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('filter_changed', {
            ...this._getUserMap(),
            number_of_search_results: listingLength,
            listing_types: filters?.selectedListingTypes ?? '',
            frequencies: filters?.selectedFrequencies ?? '',
            capacity: filters?.capacity ?? '',
            location: `${location?.lng ?? ''},${location?.lat ?? ''}`
        });
    }

    public onFiltersSelected(filterType: string, filterValue: ParamValue, source?: string): void {
        this._newEventTimestamp();
        const params: ParamMap = {
            ...this._getUserMap(),
            filter_type: filterType,
            filter_value: filterValue,
        };
        if (source) params['source'] = source;

        this._recordEvent('filter_selected', params);
    }

    /**
     * `compareUsed`: the booked listing, or its workspace, was in a comparison
     * table this session (see lib/compare/session). Sent as `compare_used`.
     */
    public onBookingRequestedDetailed(
        booking: BookingRecord,
        listingDetails: ListingDetails,
        screenUrl: string,
        compareUsed = false
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const bookingMap: ParamMap = {
            workspace_name: listingDetails.workspace?.title ?? '',
            listing_name: listingDetails.title ?? '',
            status: booking.status ?? '',
            booking_id: booking.id?.toString() ?? '',
            date: `${booking.details?.startDateTime ?? ''}-${booking.details?.endDateTime ?? ''}`,
            plan: booking.details?.unit ?? '',
            payment_method: booking.details?.paymentMethod ?? '',
            transaction_value: booking.details?.transactionValue ?? 0,
            net_payment: booking.details?.finalPrice ?? 0,
            discount_amount: booking.details?.discountValue ?? 0,
            number_of_people: booking.details?.seats ?? 0,
            compare_used: compareUsed,
        };

        const params: ParamMap = { ...this._getUserMap(), ...bookingMap };
        this._recordEvent('booking', params);
    }

    public onInitiateCheckout(totalPrice: number, currency: string, listingId: number, screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('initiate_checkout', { ...this._getUserMap(), total_price: totalPrice, currency, listing_id: listingId });
    }

    // ==========================================
    // LISTING TO BOOKING INTENT FUNNEL
    // ==========================================

    /**
     * Step 1 — User opens listing details page.
     * Properties: listing_id, workspace_id, currency, screen_url, listing_name,
     *             listing_type, listing_price, workspace_name,
     *             listing_amenities (comma-sep), provider, provider_brand
     */
    public onListingViewed(
        listingId: string | number,
        workspaceId: string | number,
        currency: string,
        listingName: string,
        listingType: string,
        listingPrice: number,
        workspaceName: string,
        listingAmenities: string[],
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('view_listing', {
            ...this._getUserMap(),
            listing_id: listingId.toString(),
            workspace_id: workspaceId.toString(),
            currency,
            listing_name: listingName,
            listing_type: listingType,
            listing_price: listingPrice,
            workspace_name: workspaceName,
            listing_amenities: listingAmenities.join(', '),
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 2 — User selects a booking unit (hour / day / month / year).
     * Properties: listing_id, provider, provider_brand, screen_url, USER_MAP
     */
    public onBookingUnitSelected(
        listingId: string | number,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('plan_selection', {
            ...this._getUserMap(),
            listing_id: listingId.toString(),
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 3 — User clicks "Select Date".
     * Properties: listing_id, workspace_id, provider, provider_brand,
     *             screen_type, USER_MAP
     */
    public onSelectDateClicked(
        listingId: string | number,
        workspaceId: string | number,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this.onScreenChangeV2('calendar_listing', screenUrl, {
            listing_id: listingId.toString(),
            workspace_id: workspaceId.toString(),
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 4 — User selects a booking date (number of months).
     * Properties: no_of_months, listing_id, provider, provider_brand,
     *             screen_url, USER_MAP
     */
    public onBookingDateSelected(
        noOfMonths: number,
        listingId: string | number,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('calendar_months_selected', {
            ...this._getUserMap(),
            no_of_months: noOfMonths,
            listing_id: listingId.toString(),
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 5 — User selects check-in time.
     * Properties: start_time, listing_id, provider, provider_brand,
     *             screen_url, USER_MAP
     */
    public onCheckinTimeSelected(
        startTime: string,
        listingId: string | number,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('calendar_checkin_time', {
            ...this._getUserMap(),
            start_time: startTime,
            listing_id: listingId.toString(),
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 6 — User selects check-out time.
     * Properties: end_time, listing_id, provider, provider_brand,
     *             screen_url, USER_MAP
     */
    public onCheckoutTimeSelected(
        endTime: string,
        listingId: string | number,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('calendar_checkout_time', {
            ...this._getUserMap(),
            end_time: endTime,
            listing_id: listingId.toString(),
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 7 — User selects number of seats / guests.
     * Properties: no_of_guests, listing_id, provider, provider_brand,
     *             screen_url, USER_MAP
     */
    public onSeatsSelected(
        noOfGuests: number,
        listingId: string | number,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('calendar_guests_selected', {
            ...this._getUserMap(),
            no_of_guests: noOfGuests,
            listing_id: listingId.toString(),
            ...this._providerMap(providerProps)
        });
    }

    // ==========================================
    // BOOKING REQUEST FUNNEL
    // ==========================================

    /**
     * Step 1 — User clicks Review Booking.
     * Properties: workspace_id, listing_id, provider, provider_brand,
     *             screen_url, USER_MAP
     */
    public onReviewBookingClicked(
        workspaceId: string | number,
        listingId: string | number,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('review_booking_clicked', {
            ...this._getUserMap(),
            workspace_id: workspaceId.toString(),
            listing_id: listingId.toString(),
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 2 — Checkout page loads.
     * Properties: screen_type="booking_review", booking_plan, provider,
     *             provider_brand, USER_MAP
     */
    public onCheckoutViewed(
        bookingPlan: string,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this.onScreenChangeV2('booking_review', screenUrl, {
            booking_plan: bookingPlan,
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 3 — User selects payment method.
     * Replaces onPaymentAdded.
     * Properties: workspace_id, listing_id, method, provider,
     *             provider_brand, screen_url, USER_MAP
     */
    public onPaymentMethodSelected(
        workspaceId: string | number,
        listingId: string | number,
        method: string,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('payment_method_selection', {
            ...this._getUserMap(),
            workspace_id: workspaceId.toString(),
            listing_id: listingId.toString(),
            method,
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Step 4 — User applies a promo code.
     * Replaces onPromoAccepted.
     * Properties: promo_name, discount, currency, provider, provider_brand,
     *             screen_url
     */
    public onPromoApplied(
        promoName: string,
        discount: number,
        currency: string,
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('apply_coupon', {
            promo_name: promoName,
            discount,
            currency,
            screen_url: screenUrl,
            ...this._providerMap(providerProps)
        });
    }

    /**
     * Steps 5 & 6 — User presses Request / Instant Booking.
     * booking_type = 'instant' fires 'submit_booking_clicked'
     * booking_type = 'request' fires 'submit_request_clicked'
     * Properties: workspace_id, listing_id, booking_type, provider,
     *             provider_brand, screen_url, USER_MAP
     */
    public onBookingSubmitted(
        workspaceId: string | number,
        listingId: string | number,
        bookingType: 'instant' | 'request',
        screenUrl: string,
        providerProps?: ProviderTrackingProps
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        const eventName =
            bookingType === 'instant'
                ? 'submit_booking_clicked'
                : 'submit_request_clicked';
        this._recordEvent(eventName, {
            ...this._getUserMap(),
            workspace_id: workspaceId.toString(),
            listing_id: listingId.toString(),
            booking_type: bookingType,
            ...this._providerMap(providerProps)
        });
    }

    // ==========================================
    // SUBSCRIPTION DISCOVERY & CONVERSION FUNNEL
    // ==========================================

    /**
     * Discovery Step 3 / Conversion Step 1 — Subscription packages are shown to the user.
     * context='discovery' → fires 'subscription_plans_viewed'
     * context='conversion' → fires 'subscription_bottom_sheet_viewed'
     * Properties: subscription_package_ids (comma-sep), language, source, USER_MAP
     */
    public onSubscriptionPackagesViewed(
        packageIds: string[],
        language: string,
        source: string,
        context: 'discovery' | 'conversion'
    ): void {
        this._newEventTimestamp();
        const eventName =
            context === 'discovery'
                ? 'subscription_plans_viewed'
                : 'subscription_bottom_sheet_viewed';
        this._recordEvent(eventName, {
            ...this._getUserMap(),
            subscription_package_ids: packageIds.join(', '),
            language,
            source
        });
    }

    /**
     * Conversion Step 2 — User selects a subscription package.
     * Replaces onSubscriptionActionTaken.
     * Properties: subscription_package, subscription_package_price, source, USER_MAP
     */
    public onSubscriptionPackageSelected(
        subscriptionPackage: string,
        subscriptionPackagePrice: number,
        source: string,
        screenUrl: string
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('subscription_plan_clicked', {
            ...this._getUserMap(),
            subscription_package: subscriptionPackage,
            subscription_package_price: subscriptionPackagePrice,
            source
        });
    }

    /**
     * Conversion Step 3 — Subscription checkout page loads.
     * Properties: screenUrl, USER_MAP
     */
    public onSubscriptionCheckoutViewed(screenUrl: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('payment_bottom_sheet_viewed', {
            ...this._getUserMap()
        });
    }

    /**
     * Conversion Step 4 — User selects a payment method on subscription checkout.
     * Properties: method, screenUrl, USER_MAP
     */
    public onSubscriptionPaymentMethodSelected(
        method: string,
        screenUrl: string
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('payment_method_changed', {
            ...this._getUserMap(),
            method
        });
    }

    /**
     * Conversion Step 5 — User presses Pay Now / purchases a subscription.
     * Properties: subscription_package_id, subscription_price, screenUrl, USER_MAP
     */
    public onSubscriptionPurchased(
        subscriptionPackageId: string,
        subscriptionPrice: number,
        screenUrl: string
    ): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);
        this._recordEvent('subscription_payment_clicked', {
            ...this._getUserMap(),
            subscription_package_id: subscriptionPackageId,
            subscription_price: subscriptionPrice
        });
    }

    public isUserSubscribed(isSubscribed: boolean, screenUrl: string, subscriptionModelId?: string, subscriptionPackageId?: string): void {
        this._newEventTimestamp();
        this._addScreenUrl(screenUrl);

        const params: ParamMap = {
            ...this._getUserMap(),
            subscription_status: isSubscribed.toString(),
        };

        if (subscriptionModelId) params['subscription_model'] = subscriptionModelId;
        if (subscriptionPackageId) params['subscription_package'] = subscriptionPackageId;

        this._recordEvent('is_user_subscribed', params);
    }

    public onHeartIconClicked(action: string, source: string, id: string, isListing: boolean): void {
        this._newEventTimestamp();

        const params: ParamMap = {
            ...this._getUserMap(),
            action,
            source,
        };

        if (isListing) {
            params['listing_id'] = id;
        } else {
            params['workspace_id'] = id;
        }

        this._recordEvent('heart_icon_clicked', params);
    }

    public onFavoritesListingClicked(listingId: string, source: string): void {
        this._newEventTimestamp();
        const params: ParamMap = { ...this._getUserMap(), listing_id: listingId, source };
        this._recordEvent('favorites_listing_clicked', params);
    }

    /**
     * User clicks "Rate experience" (My Bookings History or booking details).
     * source: post_booking | post_booking_details
     */
    public addReviewClicked(args: {
        workspaceId?: number;
        listingId?: number;
        bookingId: number;
        source: 'post_booking' | 'post_booking_details';
        screenUrl?: string;
    }): void {
        if (args.screenUrl) this._addScreenUrl(args.screenUrl);
        this._newEventTimestamp();

        const params: ParamMap = {
            ...this._getUserMap(),
            booking_id: args.bookingId,
            source: args.source
        };
        if (args.workspaceId != null) params.workspace_id = args.workspaceId;
        if (args.listingId != null) params.listing_id = args.listingId;

        this._recordEvent('add_review', params);
    }

    public submitReviewClicked(listingId: number, bookingId: number, ratingComment: string, screenUrl: string): void {
        this._addScreenUrl(screenUrl);
        this._newEventTimestamp();

        const params: ParamMap = {
            ...this._getUserMap(),
            listing_id_snake_case: listingId,
            booking_id: bookingId,
            rating_comment: ratingComment
        };

        this._recordEvent('submit_review', params);
    }

    public categoryRated(categoryName: string, ratingValue: string, screenUrl: string, bookingId: string, workspaceId?: number, listingId?: number): void {
        this._addScreenUrl(screenUrl);
        this._newEventTimestamp();

        const params: ParamMap = {
            ...this._getUserMap(),
            category: categoryName,
            category_name: categoryName,
            rating: ratingValue,
            rating_value: ratingValue,
            booking_id: bookingId
        };

        if (listingId) {
            params['listing_id'] = listingId;
            params['listing_id_snake_case'] = listingId;
        }
        if (workspaceId) {
            params['workspace_id'] = workspaceId;
            params['workspace_id_snake_case'] = workspaceId;
        }

        this._recordEvent('category_rated', params);
    }

    /**
     * User views listing reviews (details / explore / home / etc.).
     * source examples: listing_details, home, explore_map, explore_list, workspace_listings
     */
    public listingReviewsViewed(args: {
        listingId?: number;
        source: string;
        screenUrl?: string;
    }): void {
        if (args.screenUrl) this._addScreenUrl(args.screenUrl);
        this._newEventTimestamp();

        const params: ParamMap = {
            ...this._getUserMap(),
            source: args.source,
        };
        if (args.listingId != null) params.listing_id = args.listingId;

        this._recordEvent('listing_reviews', params);
    }

    /**
     * User views workspace reviews.
     * source examples: workspace_details, home, explore_list
     */
    public workspaceReviewsViewed(args: {
        workspaceId?: number;
        source: string;
        screenUrl?: string;
    }): void {
        if (args.screenUrl) this._addScreenUrl(args.screenUrl);
        this._newEventTimestamp();

        const params: ParamMap = {
            ...this._getUserMap(),
            source: args.source,
        };
        if (args.workspaceId != null) params.workspace_id = args.workspaceId;

        this._recordEvent('workspace_reviews', params);
    }

    /** My Plans — user opens the My Plans tab. */
    public onMyPlansViewed(): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_viewed', {
            ...this._getUserMap()
        });
    }

    /** My Plans — user clicks the Auto Renewal toggle (before confirmation). */
    public onMyPlansAutoRenewalToggleClicked(): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_auto_renewal_toggle_clicked', {
            ...this._getUserMap()
        });
    }

    /** My Plans — user confirms turning off auto-renewal. */
    public onMyPlansAutoRenewalTurnedOff(
        packageId: string,
        renewalDate: string
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_auto_renewal_turned_off', {
            ...this._getUserMap(),
            package_id: packageId,
            renewal_date: renewalDate
        });
    }

    /** My Plans — user cancels the auto-renewal turn-off pop-up. */
    public onMyPlansAutoRenewalCancel(packageId: string): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_auto_renewal_cancel', {
            ...this._getUserMap(),
            package_id: packageId
        });
    }

    /** My Plans — user clicks Renew Now when plan is consumed / expired. */
    public onMyPlansRenewNowClicked(
        packageId: string,
        packageName: string
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_renew_now_clicked', {
            ...this._getUserMap(),
            package_id: packageId,
            package_name: packageName
        });
    }

    /** My Plans — user clicks Upgrade. */
    public onMyPlansUpgradeClicked(
        packageId: string,
        packageName: string
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_upgrade_clicked', {
            ...this._getUserMap(),
            package_id: packageId,
            package_name: packageName
        });
    }

    /** My Plans — user selects a plan inside the Upgrade pop-up. */
    public onMyPlansUpgradePlanSelected(
        selectedPackageId: string,
        selectedPackageName: string
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_upgrade_plan_selected', {
            ...this._getUserMap(),
            selected_package_id: selectedPackageId,
            selected_package_name: selectedPackageName
        });
    }

    /** My Plans — user clicks the Add Plan button. */
    public onMyPlansAddPlanClicked(): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_add_plan_clicked', {
            ...this._getUserMap()
        });
    }

    /** My Plans — user selects a plan inside the Add Plan pop-up. */
    public onMyPlansAddPlanSelected(
        packageId: string,
        packageName: string
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_add_plan_selected', {
            ...this._getUserMap(),
            package_id: packageId,
            package_name: packageName
        });
    }

    /** My Plans — user clicks "Explore Business". */
    public onMyPlansExploreBusinessClicked(): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_explore_business_clicked', {
            ...this._getUserMap()
        });
    }

    /** My Plans — Enterprise form submitted successfully. */
    public onMyPlansEnterpriseFormSubmitted(
        companyName: string,
        companySize: string
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_enterprise_form_submitted', {
            ...this._getUserMap(),
            company_name: companyName,
            company_size: companySize
        });
    }

    /** My Plans — Enterprise form validation error shown. */
    public onMyPlansEnterpriseFormError(
        errorField: 'company_name' | 'company_size',
        errorType: 'missing' | 'invalid_character' | 'max_length' | 'min_length'
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_enterprise_form_error', {
            ...this._getUserMap(),
            error_field: errorField,
            error_type: errorType
        });
    }

    /** My Plans — user clicks the Help button. */
    public onMyPlansHelpClicked(): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_help_clicked', {
            ...this._getUserMap()
        });
    }

    /** My Plans — user clicks Explore More in the Enterprise pop-up. */
    public onMyPlansExploreMoreClicked(): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_explore_more_clicked', {
            ...this._getUserMap()
        });
    }

    /** My Plans — user is sent to checkout from a My Plans CTA. */
    public onMyPlansCheckoutRedirected(
        flow: 'renew_now' | 'upgrade' | 'add_plan',
        packageId: string
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_checkout_redirected', {
            ...this._getUserMap(),
            flow,
            package_id: packageId
        });
    }

    /** My Plans — successful purchase from a My Plans checkout flow. */
    public onMyPlansPurchaseSuccess(
        flow: 'renew_now' | 'upgrade' | 'add_plan',
        packageId: string
    ): void {
        this._newEventTimestamp();
        this._recordEvent('my_plans_purchase_success', {
            ...this._getUserMap(),
            flow,
            package_id: packageId
        });
    }

    // Compare

    /**
     * Every compare event (`compare_*`). Callers go through `useCompareTracking`,
     * which adds `compare_type` and `items_count`. `search_text` is free text,
     * so it is masked when GDPR is on, like the user variables.
     */
    public onCompareEvent(eventName: `compare_${string}`, params: ParamMap): void {
        this._newEventTimestamp();
        const masked: ParamMap =
            'search_text' in params && !this.trackUserProperties
                ? { ...params, search_text: null }
                : params;
        this._recordEvent(eventName, {
            ...this._getUserMap(),
            ...masked
        });
    }
}

export const tracking = new Tracking();
